<template>
  <transition name="fade">
    <div v-if="visible" class="fixed" @click="closePopup">
      <div class="modal-card" @click.stop>
        <div class="modal-header">
          <h2 class="title">Crear nuevo producto</h2>
          <button type="button" class="close-btn" @click="closePopup">&times;</button>
        </div>

        <span class="subtitle">Plan: <strong>{{ plan.nombre }}</strong></span>

        <form @submit.prevent="createProduct" class="form">
          <div class="form-row">
            <div class="form-group col">
              <label for="title">Título <span class="required">*</span></label>
              <input id="title" v-model="title" type="text" required maxlength="40"/>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group col">
              <label for="new-date">Fecha (opcional)</label>
              <input id="new-date" v-model="date" type="date" />
            </div>

            <div class="form-group col">
              <label for="new-hour">Hora (opcional)</label>
              <input id="new-hour" v-model="hour" type="time" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col">
              <label for="new-stock">Plazas máximas <span class="required">*</span></label>
              <input id="new-stock" v-model.number="stock" type="number" min="1" required
              />
            </div>
            <div class="form-group col">
              <label for="price">Precio <span class="required">*</span></label>
              <input id="price" v-model.number="price" type="number" min="0" step="0.01" required />
            </div>
          </div>

          <div class="buttons-container">
            <button type="button" class="secondary_button w-full" @click="closePopup">Cancelar</button>
            <button type="submit" class="main_button-green w-full">Crear producto</button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { format } from 'date-fns';
import { useLoadingStore } from "@/stores/loadingStore";
import { refreshToken } from "@/services/authService";
import { useRouter } from 'vue-router';

const router = useRouter();
const loadingStore = useLoadingStore();

const props = defineProps({
  plan: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close', 'saved', 'error']);

const visible = ref(false);
const title = ref("");
const price = ref(0);
const date = ref("");
const hour = ref("");
const stock = ref(10);

watch(
  () => props.plan,
  (newPlan) => {
    if (newPlan) {
      visible.value = true;
      title.value = "";
      date.value = "";
      hour.value = "";
      stock.value = 10;
      price.value = 0;
    } else {
      visible.value = false;
    }
  },
  { immediate: true }
);

const closePopup = () => {
  visible.value = false;
  emit('close');
};

const createProduct = async () => {
  if (!stock.value || !price.value || !title.value) {
    emit('error', 'Por favor, completa los campos requeridos.');
    return;
  }

  loadingStore.setLoading(true);
  try {
    let response = await createProductAPI();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await createProductAPI();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      loadingStore.setLoading(false);
      emit('error', "Se ha producido un error creando el producto.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      loadingStore.setLoading(false);
      emit('error', data.message);
    } else {
      loadingStore.setLoading(false);
      emit('saved', props.plan.id_promo);
      closePopup();
    }
  } catch (exception) {
    loadingStore.setLoading(false);
    emit('error', exception.message);
  }
};

const formatDateToYYYYMMDD = (val) => {
  if (!val) return '';
  if (val instanceof Date) {
    return format(val, 'yyyy-MM-dd');
  }
  const str = String(val).trim();
  const datePart = str.split('T')[0].split(' ')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }
  try {
    const parsed = new Date(str);
    if (!isNaN(parsed.getTime())) {
      return format(parsed, 'yyyy-MM-dd');
    }
  } catch (e) {
    // fallback
  }
  return datePart;
};

async function createProductAPI() {
  const formattedDate = formatDateToYYYYMMDD(date.value);

  return await fetch(process.env.VUE_APP_API_URL + "api/dashboard/plans/add_product", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id'),
      "promo_id": props.plan.id_promo,
      "title": title.value,
      "date": formattedDate,
      "hour": hour.value,
      "stock": stock.value,
      "price": price.value
    }),
  });
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 500;
  padding: 16px;
  box-sizing: border-box;
}

.modal-card {
  background-color: #ffffff;
  padding: 24px;
  border-radius: var(--radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--very-clear-gray, #eeeeee);
  padding-bottom: 12px;
  margin-bottom: 18px;
}

.title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-color-dark, #2f4858);
  margin: 0;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: var(--clear-gray, #607d94);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;
}

.close-btn:hover {
  color: var(--text-color-dark, #2f4858);
  background-color: var(--almost-white, #f5f5f5);
}

.subtitle {
  font-size: 14px;
  color: var(--text-color-dark, #2f4858);
  margin: 0;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 30px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color-dark, #2f4858);
}

.required {
  color: #e53e3e;
}

.form-row {
  display: flex;
  gap: 12px;
}

.col {
  flex: 1;
}

.buttons-container {
  display: flex;
  gap: 12px;
  margin-top: 1rem;
}

.w-full {
  width: 100%;
}
</style>
